import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Icons from "lucide-react";
import { pickLocale } from "@/lib/helpers";
import { track } from "@/api/analytics";
import { resolveImageUrl } from "@/components/ImageUploader";

/** Card in the POI list of a category. */
export default function POICard({ poi }) {
  const { i18n, t } = useTranslation();
  const cover = resolveImageUrl(poi.images?.[0]);
  const Icon = poi.icon ? (Icons[poi.icon] || Icons.MapPin) : Icons.MapPin;

  return (
    <Link
      to={`/poi/${poi.id}`}
      onClick={() => track("view_poi", { target_type: "poi", target_id: poi.id })}
      data-testid={`poi-card-${poi.id}`}
      className={`group relative flex gap-4 bg-white rounded-2xl border ${
        poi.featured ? "border-bronze-200/70 shadow-editorial" : "border-ink-200/40 shadow-soft"
      } overflow-hidden hover:-translate-y-0.5 hover:shadow-elevated hover:border-bronze-200 active:scale-[0.99] transition-all`}
    >
      {cover ? (
        <div className="w-24 shrink-0 bg-ivory-100">
          <img src={cover} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-24 shrink-0 bg-ivory-100 flex items-center justify-center">
          <Icon strokeWidth={1.3} className="w-7 h-7 text-bronze-400 opacity-80" />
        </div>
      )}
      <div className="flex-1 min-w-0 py-3.5 pr-4">
        {poi.featured && (
          <span className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-overline text-bronze-500 bg-bronze-50 rounded-full px-2 py-0.5 mb-1.5">
            <Icons.Star strokeWidth={1.8} className="w-2.5 h-2.5 text-bronze-500" /> {t("poi.featured")}
          </span>
        )}
        <div className="font-editorial italic text-[22px] text-ink-900 leading-[1.15] line-clamp-2">
          {pickLocale(poi.title, i18n.language)}
        </div>
        {pickLocale(poi.subtitle, i18n.language) && (
          <div className="text-xs text-ink-500 mt-1 line-clamp-1">
            {pickLocale(poi.subtitle, i18n.language)}
          </div>
        )}
        {poi.address && (
          <div className="flex items-center gap-1 mt-1.5 text-[11px] text-ink-300 truncate">
            <Icons.MapPin strokeWidth={1.4} className="w-3 h-3 shrink-0" />
            <span className="truncate">{poi.address}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
